import template from './suggestion-list.html.twig';
import '../../component/suggestion-form';

const { Component, Mixin } = Shopware;
const { Criteria } = Shopware.Data;

Component.register('topdata-es-suggestion-list', {
    template,

    inject: ['repositoryFactory'],

    mixins: [
        Mixin.getByName('listing'),
    ],

    data() {
        return {
            items: null,
            showFormModal: false,
            currentEntity: null,
            isLoading: true,
            sortBy: 'priority',
            sortDirection: 'ASC',
            limit: 25,
            targetLabels: {},
            filterLanguage: null,
            filterSource: null,
        };
    },

    metaInfo() {
        return { title: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.title') };
    },

    computed: {
        repository() {
            return this.repositoryFactory.create('tdes_search_suggestion');
        },

        languageOptions() {
            return [{
                value: null,
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.filterLanguageAll'),
            }, {
                value: 'de',
                label: 'Deutsch',
            }, {
                value: 'en',
                label: 'English',
            }, {
                value: 'fr',
                label: 'Français',
            }, {
                value: 'NULL',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.filterLanguageGlobal'),
            }];
        },

        sourceOptions() {
            return [{
                value: null,
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.filterSourceAll'),
            }, {
                value: 'MANUAL',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.source.MANUAL'),
            }, {
                value: 'TITLE_MINING',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.source.TITLE_MINING'),
            }];
        },

        columns() {
            return [{
                property: 'term',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.column.term'),
                allowResize: false,
                primary: true,
                sortable: true,
            }, {
                property: 'title',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.column.title'),
                allowResize: true,
                sortable: true,
            }, {
                property: 'source',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.column.source'),
                allowResize: false,
                sortable: true,
            }, {
                property: 'languageId',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.column.language'),
                allowResize: false,
                sortable: true,
            }, {
                property: 'targetType',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.column.targetType'),
                allowResize: false,
            }, {
                property: 'targetUrl',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.column.targetUrl'),
                allowResize: true,
            }, {
                property: 'targetQuery',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.column.targetQuery'),
                allowResize: true,
            }, {
                property: 'priority',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.column.priority'),
                allowResize: false,
                sortable: true,
            }, {
                property: 'active',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.column.active'),
                allowResize: false,
            }, {
                property: 'createdAt',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.column.createdAt'),
                allowResize: false,
                sortable: true,
            }];
        },
    },

    methods: {
        targetTypeLabel(type) {
            const key = `TopdataEnhancedSearchSW6.topdata-es-suggestion.type.${type}`;
            return this.$tc(key) === key ? type : this.$tc(key);
        },

        targetTypeVariant(type) {
            const variants = {
                category: 'info',
                product: 'success',
                custom: 'warning',
                search: 'info',
            };
            return variants[type] ?? 'neutral';
        },

        // The resolved storefront URL (SEO pretty URL + appended query string, e.g. the
        // pre-set listing filters) is generated server-side by the suggestion redirect route —
        // building it here from the stored target would hit a canonical 301 that drops the query.
        getFinalUrl(item) {
            return `${window.location.origin}/enhanced-search/suggestion/${item.id}`;
        },

        onOpenItem(item) {
            const url = this.getFinalUrl(item);
            if (url) {
                window.open(url, '_blank', 'noopener');
            }
        },

        getList() {
            this.isLoading = true;

            const criteria = new Criteria(this.page, this.limit);
            criteria.addSorting(Criteria.sort(this.sortBy, this.sortDirection, this.naturalSorting));

            if (this.filterLanguage) {
                if (this.filterLanguage === 'NULL') {
                    criteria.addFilter(Criteria.equals('languageId', null));
                } else {
                    criteria.addFilter(Criteria.equals('languageId', this.filterLanguage));
                }
            }

            if (this.filterSource) {
                criteria.addFilter(Criteria.equals('source', this.filterSource));
            }

            return this.repository.search(criteria).then((result) => {
                this.total = result.total;
                this.items = result;
                this.isLoading = false;
                return this.resolveTargetLabels(result);
            }).catch(() => {
                this.isLoading = false;
            });
        },

        getTargetIdFromUrl(item) {
            const url = item.targetUrl || '';
            if (item.targetType === 'category') {
                const m = /^\/navigation\/([0-9a-f]{32})$/i.exec(url);
                return m ? m[1] : null;
            }
            if (item.targetType === 'product') {
                const m = /^\/detail\/([0-9a-f]{32})$/i.exec(url);
                return m ? m[1] : null;
            }
            return null;
        },

        getEntityLabel(entity) {
            return (entity.translated && entity.translated.name) || entity.name;
        },

        getCategoryBreadcrumb(category) {
            const breadcrumb = category.breadcrumb || {};
            const path = Object.values(breadcrumb).slice(1);
            if (path.length === 0) {
                return this.getEntityLabel(category);
            }
            return path.join(' > ');
        },

        targetLabelFor(item) {
            if (item.targetType === 'search') {
                return `/search?search=${encodeURIComponent(item.term || '')}`;
            }
            const targetId = this.getTargetIdFromUrl(item);
            if (targetId) {
                const resolved = this.targetLabels[targetId];
                if (resolved) {
                    return resolved;
                }
            }
            return item.targetUrl || '—';
        },

        resolveTargetLabels(items) {
            this.targetLabels = {};

            const productIds = new Set();
            const categoryIds = new Set();

            items.forEach((item) => {
                const id = this.getTargetIdFromUrl(item);
                if (!id) {
                    return;
                }
                if (item.targetType === 'product') {
                    productIds.add(id);
                } else if (item.targetType === 'category') {
                    categoryIds.add(id);
                }
            });

            const groups = [{
                entity: 'product',
                ids: [...productIds],
                resolve: (entity) => this.getEntityLabel(entity),
            }, {
                entity: 'category',
                ids: [...categoryIds],
                resolve: (entity) => this.getCategoryBreadcrumb(entity),
            }];

            return Promise.all(groups
                .filter((group) => group.ids.length > 0)
                .map((group) => this.loadTargetLabels(group)));
        },

        async loadTargetLabels({ entity, ids, resolve }) {
            const repository = this.repositoryFactory.create(entity);
            const criteria = new Criteria(1, ids.length);
            criteria.addFilter(Criteria.equalsAny('id', ids));

            try {
                const result = await repository.search(criteria);
                result.forEach((target) => {
                    this.targetLabels[target.id] = resolve(target);
                });
            } catch {
            }
        },
        onPageChange(params) {
            this.page = params.page;
            this.limit = params.limit;
            this.getList();
        },

        onSortColumn(column) {
            this.sortBy = column.dataIndex ?? column.property;
            this.sortDirection = column.sortDirection ?? 'ASC';
            this.getList();
        },

        getLanguageLabel(languageId) {
            if (!languageId) {
                return this.$tc('TopdataEnhancedSearchSW6.topdata-es-suggestion.languageGlobal');
            }
            const labels = { de: 'Deutsch', en: 'English', fr: 'Français' };
            return labels[languageId] || languageId;
        },

        getSourceLabel(source) {
            const key = `TopdataEnhancedSearchSW6.topdata-es-suggestion.source.${source}`;
            return this.$tc(key) === key ? (source || '—') : this.$tc(key);
        },

        onFilterChange() {
            this.page = 1;
            this.getList();
        },

        onAddSuggestion() {
            this.currentEntity = this.repository.create();
            this.currentEntity.active = true;
            this.currentEntity.priority = 0;
            this.currentEntity.targetType = 'custom';
            this.currentEntity.source = 'MANUAL';
            this.showFormModal = true;
        },

        onEditSuggestion(item) {
            this.repository.get(item.id).then((entity) => {
                this.currentEntity = entity;
                this.showFormModal = true;
            });
        },

        onCloseFormModal() {
            this.showFormModal = false;
            this.currentEntity = null;
            this.getList();
        },

        onDeleteSuggestion(item) {
            this.repository.delete(item.id).then(() => {
                this.getList();
            });
        },
    },

    mounted() {
        this.getList();
    },
});
